import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { X, Loader2, FileText, Download, Upload, Trash2, ExternalLink, Save, CheckCircle2, Gauge, ThumbsUp, ShieldAlert, Lightbulb } from "lucide-react";
import { api } from "../../lib/api";
import { SECTIONS, SUPPORTING_DOCS, BLANK_PDF_URL, buildRendered } from "../../data/healthQuestionnaire";

const API = process.env.REACT_APP_BACKEND_URL;

function Field({ f, value, onChange }) {
  const base = "w-full text-sm rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 focus:border-[#c9a961] focus:outline-none transition-colors duration-300";
  if (f.type === "long") {
    return <textarea data-testid={`q-${f.k}`} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />;
  }
  if (f.type === "num") {
    return <input data-testid={`q-${f.k}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} />;
  }
  if (f.type === "date") {
    return <input data-testid={`q-${f.k}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />;
  }
  if (f.type === "yesno") {
    return (
      <div className="flex gap-2">
        {["Yes", "No"].map((opt) => {
          const v = opt.toLowerCase();
          const on = value === v;
          return (
            <button key={opt} type="button" data-testid={`q-${f.k}-${v}`} onClick={() => onChange(on ? "" : v)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.14em] transition-colors duration-300 ${on ? "bg-[#1e3d3a] text-white" : "border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:border-[#c9a961]"}`}>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <select data-testid={`q-${f.k}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
        <option value="">Select…</option>
        {f.choices.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  // text or check (comma-separated)
  return <input data-testid={`q-${f.k}`} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
    placeholder={f.type === "check" ? "comma-separated" : ""} className={base} />;
}

export default function HealthQuestionnaire({ leadId, lead, onClose, onSaved }) {
  const [answers, setAnswers] = useState({});
  const [meta, setMeta] = useState({ client: "", date: new Date().toISOString().slice(0, 10), completed_by: "" });
  const [documents, setDocuments] = useState([]);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [score, setScore] = useState(null);
  const [scoring, setScoring] = useState(false);
  const fileInputs = useRef({});

  useEffect(() => {
    api.get(`/admin/leads/${leadId}/questionnaire`).then(({ data }) => {
      setAnswers(data.answers || {});
      setDocuments(data.documents || []);
      setPdf(data.pdf || null);
      setScore(data.score || null);
      setMeta((m) => ({
        client: data.meta?.client || lead?.company || answersName(data.answers) || "",
        date: data.meta?.date || m.date,
        completed_by: data.meta?.completed_by || "",
      }));
    }).catch(() => {
      setMeta((m) => ({ ...m, client: lead?.company || "" }));
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const answersName = (a) => (a && a.biz_name) || "";
  const setA = (k, v) => setAnswers((prev) => ({ ...prev, [k]: v }));

  const dl = (path, filename) => {
    const url = `${API}/api/admin/questionnaire-file/download?path=${encodeURIComponent(path)}&filename=${encodeURIComponent(filename || "file")}`;
    window.open(url, "_blank");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/questionnaire`, {
        answers, rendered: buildRendered(answers), meta,
      });
      setPdf(data.pdf || null);
      toast.success("Questionnaire saved — health score is generating and will appear in the Health Score tab shortly.");
      onSaved?.();
    } catch {
      toast.error("Could not save the questionnaire.");
    } finally {
      setSaving(false);
    }
  };

  const generateScore = async () => {
    setScoring(true);
    try {
      // Persist the latest answers first so the score reflects what's on screen.
      const { data: saved } = await api.post(`/admin/leads/${leadId}/questionnaire?skip_autoscore=1`, {
        answers, rendered: buildRendered(answers), meta,
      });
      setPdf(saved.pdf || null);
      const { data } = await api.post(`/admin/leads/${leadId}/questionnaire/score`);
      setScore(data);
      toast.success(`Business Health Score: ${data.overall_score}/100 — saved to the Health Score tab.`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the health score.");
    } finally {
      setScoring(false);
    }
  };

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      const { data } = await api.post(`/admin/leads/${leadId}/questionnaire/document`, fd);
      setDocuments((d) => [...d, data]);
      toast.success(`${docType} uploaded.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingType(null);
    }
  };

  const removeDoc = async (docId) => {
    try {
      await api.delete(`/admin/leads/${leadId}/questionnaire/document/${docId}`);
      setDocuments((d) => d.filter((x) => x.id !== docId));
    } catch { toast.error("Could not remove document."); }
  };

  const docsByType = (t) => documents.filter((d) => d.doc_type === t);

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-[#1e3d3a]/60 backdrop-blur-sm"
      data-testid="health-questionnaire-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full max-w-3xl h-[92vh] rounded-2xl bg-[#f5f3ec] shadow-2xl border border-[#c9a961]/25 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#1e3d3a]/10 bg-white">
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-[0.24em] text-[#a3823f]">Elevare</p>
            <h2 className="font-head text-xl text-[#1e3d3a] truncate">Business Health Questionnaire</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a href={BLANK_PDF_URL} target="_blank" rel="noreferrer" data-testid="q-view-blank"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
              <ExternalLink className="h-3.5 w-3.5" /> Blank PDF
            </a>
            {pdf && (
              <button type="button" data-testid="q-download-pdf" onClick={() => dl(pdf.path, pdf.filename)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#c9a961] bg-white px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#a3823f] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
                <Download className="h-3.5 w-3.5" /> Completed PDF
              </button>
            )}
            <button type="button" data-testid="q-close" onClick={onClose} className="text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300 p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-7 w-7 text-[#c9a961] animate-spin" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Meta */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-xl border border-[#1e3d3a]/12 bg-white p-4">
              <label className="text-xs text-[#1e3d3a]/60">Client / Business
                <input data-testid="q-meta-client" value={meta.client} onChange={(e) => setMeta({ ...meta, client: e.target.value })}
                  className="mt-1 w-full text-sm rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 focus:border-[#c9a961] focus:outline-none" />
              </label>
              <label className="text-xs text-[#1e3d3a]/60">Assessment Date
                <input data-testid="q-meta-date" type="date" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })}
                  className="mt-1 w-full text-sm rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 focus:border-[#c9a961] focus:outline-none" />
              </label>
              <label className="text-xs text-[#1e3d3a]/60">Completed By
                <input data-testid="q-meta-by" value={meta.completed_by} onChange={(e) => setMeta({ ...meta, completed_by: e.target.value })}
                  className="mt-1 w-full text-sm rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 focus:border-[#c9a961] focus:outline-none" />
              </label>
            </div>

            {/* Business Health Score */}
            <HealthScorePanel score={score} />

            {SECTIONS.map((s, si) => (
              <div key={s.id} data-testid={`q-section-${s.id}`} className="rounded-xl border border-[#1e3d3a]/12 bg-white p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#a3823f]">Section {si + 1}</p>
                <h3 className="font-head text-lg text-[#1e3d3a] mt-0.5">{s.title}</h3>
                <p className="text-xs text-[#1e3d3a]/45 mt-1 mb-4">{s.purpose}</p>
                <div className="space-y-4">
                  {s.fields.map((f) => (
                    <div key={f.k}>
                      <label className="block text-sm text-[#1e3d3a]/75 mb-1.5">{f.label}</label>
                      <Field f={f} value={answers[f.k]} onChange={(v) => setA(f.k, v)} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Supporting documents */}
            <div data-testid="q-supporting-docs" className="rounded-xl border border-[#c9a961]/35 bg-white p-5">
              <h3 className="font-head text-lg text-[#1e3d3a]">Supporting Documents</h3>
              <p className="text-xs text-[#1e3d3a]/45 mt-1 mb-4">Upload any documents that apply — they upload into the CRM record when you add this business as a client.</p>
              <div className="space-y-2">
                {SUPPORTING_DOCS.map((t) => {
                  const has = docsByType(t);
                  return (
                    <div key={t} className="flex items-center gap-2 flex-wrap border-b border-[#1e3d3a]/8 pb-2">
                      <span className="text-sm text-[#1e3d3a]/75 flex-1 min-w-[180px]">{t}</span>
                      {has.map((d) => (
                        <span key={d.id} className="inline-flex items-center gap-1 rounded-full bg-[#eae7de] px-2.5 py-1 text-[11px] text-[#1e3d3a]">
                          <FileText className="h-3 w-3" />
                          <button type="button" onClick={() => dl(d.storage_path, d.filename)} className="hover:text-[#a3823f] max-w-[140px] truncate">{d.filename}</button>
                          <button type="button" data-testid={`q-doc-remove-${d.id}`} onClick={() => removeDoc(d.id)} className="text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3 w-3" /></button>
                        </span>
                      ))}
                      <input type="file" hidden ref={(el) => (fileInputs.current[t] = el)}
                        onChange={(e) => { uploadDoc(t, e.target.files?.[0]); e.target.value = ""; }} />
                      <button type="button" data-testid={`q-doc-upload-${t.replace(/[^A-Za-z0-9]+/g, "-")}`}
                        onClick={() => fileInputs.current[t]?.click()} disabled={uploadingType === t}
                        className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/70 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-50">
                        {uploadingType === t ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-[#1e3d3a]/10 bg-white">
          <span className="text-[11px] text-[#1e3d3a]/45">{documents.length} document{documents.length === 1 ? "" : "s"} attached</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#1e3d3a]/50 hover:text-[#1e3d3a] transition-colors duration-300">Close</button>
            <button type="button" data-testid="q-generate-score" onClick={generateScore} disabled={scoring || saving}
              className="inline-flex items-center gap-2 rounded-full border border-[#c9a961] bg-[#c9a961]/10 text-[#a3823f] px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60">
              {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gauge className="h-4 w-4" />}
              {scoring ? "Scoring…" : "Generate health score"}
            </button>
            <button type="button" data-testid="q-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-6 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : "Save questionnaire"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const scoreColor = (n) =>
  n >= 80 ? "#4b7a3a" : n >= 60 ? "#6b7c3a" : n >= 40 ? "#c9a961" : n >= 20 ? "#d08a3a" : "#b23a48";

function HealthScorePanel({ score }) {
  if (!score) return null;
  const overall = Number(score.overall_score) || 0;
  const col = scoreColor(overall);
  return (
    <div data-testid="q-score-panel" className="rounded-xl border-2 p-5" style={{ borderColor: col + "66", background: "#fff" }}>
      <div className="flex items-start gap-5 flex-wrap">
        <div className="text-center shrink-0">
          <div className="relative h-24 w-24">
            <svg viewBox="0 0 36 36" className="h-24 w-24 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#eae7de" strokeWidth="3.4" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke={col} strokeWidth="3.4"
                strokeDasharray={`${overall} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-head text-2xl" style={{ color: col }}>{overall}</span>
              <span className="text-[8px] uppercase tracking-[0.14em] text-[#1e3d3a]/40">/ 100</span>
            </div>
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em]" style={{ color: col }}>{score.band}</div>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">
            <Gauge className="h-3.5 w-3.5" /> Business Health Score
          </div>
          <p className="mt-2 text-sm text-[#1e3d3a]/75 leading-relaxed">{score.summary}</p>
        </div>
      </div>

      {(score.sections || []).length > 0 && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {score.sections.map((s, i) => {
            const c = scoreColor(Number(s.score) || 0);
            return (
              <div key={i} data-testid={`q-score-section-${i}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#1e3d3a]/75">{s.title}</span>
                  <span className="tabular-nums font-medium" style={{ color: c }}>{s.score} · {s.rating}</span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-[#eae7de] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${s.score}%`, background: c }} />
                </div>
                {s.note && <p className="mt-1 text-[11px] text-[#1e3d3a]/45">{s.note}</p>}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ScoreList title="Strengths" icon={ThumbsUp} tone="#4b7a3a" items={score.strengths} />
        <ScoreList title="Risks" icon={ShieldAlert} tone="#b23a48" items={score.risks} />
        <ScoreList title="Recommendations" icon={Lightbulb} tone="#a3823f" items={score.recommendations} />
      </div>
      {score.generated_at && (
        <p className="mt-3 text-[10px] text-[#1e3d3a]/35">Generated {new Date(score.generated_at).toLocaleString()}</p>
      )}
    </div>
  );
}

function ScoreList({ title, icon: Icon, tone, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] mb-1.5" style={{ color: tone }}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-[12px] text-[#1e3d3a]/70 leading-snug flex gap-1.5">
            <span style={{ color: tone }}>•</span> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
